import React from "react";
import { ModalTypes } from "../@types/common";
import Modal from "../components/Elements/Modal";

type ModalContextType = {
  modal?: ModalTypes;
  updateModal: CallableFunction;
  meta?: unknown;
};

const EMPTY_MODAL_STATE: ModalContextType = {
  modal: undefined,
  meta: undefined,
  updateModal: () => void 0,
};

// Empty function
// This has to be wrapped twice, since setState can also take a function to set the state.
/* istanbul ignore next */
const NOOP_FUNCTION = () => () => void 0;

const ModalContext = React.createContext<ModalContextType>(EMPTY_MODAL_STATE);
export const useModal = () => React.useContext(ModalContext);

export const ModalProvider = ({ children }: { children?: React.ReactNode }) => {
  const [modal, setModal] = React.useState<ModalTypes | undefined>();
  const [meta, setMeta] = React.useState<unknown | undefined>();
  const [onClose, setOnClose] = React.useState<CallableFunction>(NOOP_FUNCTION);

  /**
   * updateModal wraps around the setModal function while also allowing the caller to provide a closeHandler.
   *
   * If provider the closeHandler will be called when then modal is closed.
   * This allows for actions to be taken after the user clicks to accept the modal.
   */
  const updateModal = React.useCallback((modalType: ModalTypes, closeHandler: CallableFunction, meta?: unknown) => {
    setModal(modalType);
    setMeta(meta);
    closeHandler &&
      setOnClose(() => () => {
        closeHandler();
        setOnClose(NOOP_FUNCTION);
      });
  }, []);

  return (
    <ModalContext.Provider value={{ modal, updateModal, meta }}>
      <Modal data-testid="modal-container" onCloseHandler={onClose} meta={meta} />
      {children}
    </ModalContext.Provider>
  );
};
